import { SelectionModel } from '@angular/cdk/collections';
import { Component, Input, SimpleChanges, EventEmitter, Output, OnChanges } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatTableDataSource } from '@angular/material/table';
import {
    BasicMachineFunctionsService,
    t_FunctionConfig,
} from 'src/app/services/apiHandler/basicMachineFunctions/basic-machine-functions.service';
import { t_DataIdentifier, t_DataPointTimeSeries } from 'src/app/services/apiHandler/did-data-retrieval/did-data-retrieval.service';
@Component({
    selector: 'app-basic-view-table',
    templateUrl: './basic-view-table.component.html',
    styleUrls: ['./basic-view-table.component.scss'],
})
export class BasicViewTableComponent implements OnChanges {
    @Input() rawDataset?: t_DataPointTimeSeries[];
    @Input() functionConfig?: t_FunctionConfig;
    @Output() dataSelected = new EventEmitter<t_OutputCheckedElement>();

    public displayedColumns: string[] = [/*'ecu_id', 'data_id', */ 'checkbox', 'data_name', 'value', 'timestamp'];
    public rawDatasetLoaded: boolean = false;
    public functionConfigLoaded: boolean = false;
    public tableDataset: MatTableDataSource<t_TableDataPoint> = new MatTableDataSource<t_TableDataPoint>();
    private tableDataSetInitialized: boolean = false;
    public selection: SelectionModel<t_TableDataPoint> = new SelectionModel<t_TableDataPoint>(true, []);

    constructor(private functionsService: BasicMachineFunctionsService) {}

    // Detect changes in input Variables and Update dependent variables in class
    ngOnChanges(changes: SimpleChanges): void {
        if ('rawDataset' in changes) {
            if (this.rawDataset && this.rawDataset.length > 0) {
                this.rawDatasetLoaded = true;
                if (!this.tableDataSetInitialized) {
                    this.initTableDataSet(this.rawDataset);
                    this.tableDataSetInitialized = true;
                }
                this.updateTableDataset(this.rawDataset);
            }
        }
        if ('functionConfig' in changes) {
            if (this.functionConfig && this.functionConfig.dataNames.length > 0) {
                this.functionConfigLoaded = true;
            }
        }
    }

    private initTableDataSet(newData: t_DataPointTimeSeries[]) {
        let newTableData: t_TableDataPoint[] = [];
        newTableData = this.extractLatestValuesFromTimeSeries(newData);

        this.tableDataset.data = newTableData;
    }

    private updateTableDataset(newData: t_DataPointTimeSeries[]) {
        let newTableData: t_TableDataPoint[] = [];

        newTableData = this.extractLatestValuesFromTimeSeries(newData);

        newTableData.forEach((_newTableData) => {
            let tableDataIndex: number = this.tableDataset.data.findIndex((dataPoint) => {
                let dataPointFound: boolean =
                    dataPoint.identifier.ecu_id == _newTableData.identifier.ecu_id &&
                    dataPoint.identifier.data_id == _newTableData.identifier.data_id;

                return dataPointFound;
            });

            if (tableDataIndex >= 0) {
                this.tableDataset.data[tableDataIndex].timestamp = _newTableData.timestamp;
                this.tableDataset.data[tableDataIndex].value = _newTableData.value;
            } else {
                console.warn('Datapoint ' + _newTableData.name + ' not found in table data. Start initialization again.');
                this.initTableDataSet(newData);
            }
        });
    }

    private extractLatestValuesFromTimeSeries(timeSeriesData: t_DataPointTimeSeries[]): t_TableDataPoint[] {
        let tableDataPoints: t_TableDataPoint[] = [];

        timeSeriesData.forEach((dataPoint) => {
            // Get Name
            let name: string = this.functionsService.getDataNameOfIdentifier(dataPoint.identifier);
            if (name == '') {
                name = 'NAME NOT FOUND';
            }

            // Get unit of data
            let unit: string | undefined = this.functionsService.didConfigConnection.getDidInformation(dataPoint.identifier)?.unit;
            if (unit == undefined) unit = 'Unit not found';

            // Get status of checkbox
            let checked: boolean = this.functionsService.getCheckedStatus(this.functionConfig!.name, dataPoint.identifier);

            // Store in new TableDataPoint
            let newTableDataPoint: t_TableDataPoint;

            if (dataPoint.data && dataPoint.data.length > 0) {
                
                // Get Latest Data Value
                let lastIndex: number = dataPoint.data?.length - 1;

                // Get converted value
                let normalizedValue: number = this.normalizedValue(
                    dataPoint.data[lastIndex].value[0],
                    this.functionsService.didConfigConnection.getDidInformation(dataPoint.identifier)!.conversionFactor,
                    this.functionsService.didConfigConnection.getDidInformation(dataPoint.identifier)!.conversionOffset
                );

                newTableDataPoint = {
                    identifier: dataPoint.identifier,
                    timestamp: dataPoint.data[lastIndex].timestamp,
                    value: normalizedValue,
                    name: name,
                    unit: unit,
                    checked: checked,
                };                
            } else {
                newTableDataPoint = {
                    identifier: dataPoint.identifier,
                    timestamp: new Date(0),
                    value: 0,
                    name: name,
                    unit: unit,
                    checked: checked,
                };
            }
            tableDataPoints.push(newTableDataPoint);
        });
        return tableDataPoints;
    }

    private normalizedValue(value: number, factor: number, offset: number) {
        if(factor == 0) {
            factor = 1;
        }
        let res: number = (value * factor) + offset;
        res = parseFloat(res.toFixed(1));
        return res;
    }

    public dataPointCheck(row: any, _event: MatCheckboxChange) {
        if(_event.checked) {
            row.checked = true;
            this.selection.select(row);
            this.toggleDataPointChecked(row.identifier.ecu_id, row.identifier.data_id, row.name!, row.checked);
        } else {
            row.checked = false;
            this.selection.deselect(row);
            this.toggleDataPointChecked(row.identifier.ecu_id, row.identifier.data_id, row.name!, row.checked);
        }
    }

    public toggleDataPointChecked(_ecuId: number, _dataId: number, _name: string, _checked: boolean) {
        // Emit Output Event with checked Identifier and status
        let output: t_OutputCheckedElement = {
            identifier: { ecu_id: _ecuId, data_id: _dataId },
            name: _name,
            checked: _checked,
        };
        this.dataSelected.emit(output);
    }

    public masterToggle() {
        this.isAllSelected() ? this.removeAll() : this.addToGraph();
    }

    public isAllSelected() {
        const numSelected = this.selection.selected.length;
        const numRows = this.functionConfig?.dataNames.length;
        return numSelected === numRows;
    }

    public removeAll() {
        this.selection.clear();
        this.tableDataset.data.forEach(row => {
            row.checked = false;
            this.toggleDataPointChecked(row.identifier.ecu_id, row.identifier.data_id, row.name!, row.checked);
        });
    }

    public addToGraph() { 
        this.tableDataset.data.forEach(row => {
            row.checked = true;
            this.selection.select(row);
            this.toggleDataPointChecked(row.identifier.ecu_id, row.identifier.data_id, row.name!, row.checked);
        });
    }
}

/* ================================================ */
/* ==================== TYPES ===================== */
/* ================================================ */

type t_TableDataPoint = {
    identifier: t_DataIdentifier;
    name?: string;
    timestamp: Date;
    value: number;
    unit: string;
    checked: boolean;
};

export type t_OutputCheckedElement = {
    identifier: t_DataIdentifier;
    name: string;
    checked: boolean;
};
